import { useState, useEffect } from 'react';
import { Calendar, XCircle, Edit, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ManageTimeOff = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('awaiting');
  const [timeOffRequests, setTimeOffRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editForm, setEditForm] = useState({
    first_day: '',
    first_day_type: 'full_day',
    last_day: '',
    last_day_type: 'full_day',
    notes: ''
  });

  useEffect(() => {
    fetchTimeOffRequests();
  }, []);

  const fetchTimeOffRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/time-off-requests/my-requests`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch requests');
      }

      const data = await response.json();
      setTimeOffRequests(data);
    } catch (error) {
      toast.error('Failed to load time off requests');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelRequest = async (requestId) => {
    if (!window.confirm('Are you sure you want to cancel this time off request?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/time-off-requests/${requestId}/cancel`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to cancel request');
      }

      toast.success('Time off request cancelled');
      fetchTimeOffRequests();
    } catch (error) {
      toast.error('Failed to cancel request');
    }
  };

  const handleEditClick = (request) => {
    setSelectedRequest(request);
    setEditForm({
      first_day: request.first_day,
      first_day_type: request.first_day_type,
      last_day: request.last_day,
      last_day_type: request.last_day_type,
      notes: request.notes || ''
    });
    setShowEditDialog(true);
  };

  const handleEditSubmit = async () => {
    if (!selectedRequest) return;

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/time-off-requests/${selectedRequest.id}/edit`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(editForm)
      });

      if (!response.ok) {
        throw new Error('Failed to update request');
      }

      toast.success('Time off request updated successfully');
      setShowEditDialog(false);
      fetchTimeOffRequests();
    } catch (error) {
      toast.error('Failed to update request');
    }
  };

  const getFilteredRequests = () => {
    switch (activeTab) {
      case 'approved':
        return timeOffRequests.filter(r => r.status === 'approved');
      case 'awaiting':
        return timeOffRequests.filter(r => r.status === 'pending');
      case 'rejected':
        return timeOffRequests.filter(r => r.status === 'rejected');
      default:
        return [];
    }
  };

  const filteredRequests = getFilteredRequests();

  return (
    <section className="space-y-6">
      {/* Header */}
      <div className="w-full bg-white rounded-3xl shadow-lg p-6">
          <div className="flex items-center gap-4">
            <Button onClick={() => navigate('/profile')} variant="ghost" size="sm">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Manage Time Off</h1>
              <p className="text-sm text-gray-500">View and manage your time off requests</p>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="w-full bg-white rounded-3xl shadow-lg overflow-hidden">
          <div className="border-b border-gray-200">
            <div className="flex">
              <button
                onClick={() => setActiveTab('awaiting')}
                className={`flex-1 px-2 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-base font-semibold transition-colors ${
                  activeTab === 'awaiting'
                    ? 'text-amber-700 border-b-2 border-amber-500 bg-amber-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                <span className="hidden sm:inline">Awaiting Approval</span>
                <span className="sm:hidden">Awaiting</span>
              </button>
              <button
                onClick={() => setActiveTab('approved')}
                className={`flex-1 px-2 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-base font-semibold transition-colors ${
                  activeTab === 'approved'
                    ? 'text-green-700 border-b-2 border-green-500 bg-green-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Approved
              </button>
              <button
                onClick={() => setActiveTab('rejected')}
                className={`flex-1 px-2 sm:px-6 py-3 sm:py-4 text-center text-xs sm:text-base font-semibold transition-colors ${
                  activeTab === 'rejected'
                    ? 'text-red-700 border-b-2 border-red-500 bg-red-50'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                }`}
              >
                Rejected
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6">
            {loading ? (
              <div className="text-center py-12">
                <p className="text-gray-500">Loading...</p>
              </div>
            ) : filteredRequests.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-500 text-lg">No {activeTab} requests</p>
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {filteredRequests.map((request) => (
                  <div
                    key={request.id}
                    className={`w-full p-4 sm:p-6 rounded-3xl shadow-lg border-l-4 ${
                      request.status === 'approved' ? 'bg-green-50 border-green-500' :
                      request.status === 'rejected' ? 'bg-red-50 border-red-500' :
                      'bg-amber-50 border-amber-500'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 sm:gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 mb-2">
                          <p className="text-base sm:text-lg font-semibold text-gray-900 break-words">
                            {new Date(request.first_day).toLocaleDateString("en-GB")} - {new Date(request.last_day).toLocaleDateString("en-GB")}
                          </p>
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold w-fit ${
                            request.status === 'approved' ? 'bg-green-500 text-white' :
                            request.status === 'rejected' ? 'bg-red-500 text-white' :
                            'bg-amber-500 text-white'
                          }`}>
                            {request.status.charAt(0).toUpperCase() + request.status.slice(1)}
                          </span>
                        </div>
                        <p className="text-xs sm:text-sm text-gray-600 mb-2 break-words">
                          {request.total_days} day{request.total_days !== 1 ? 's' : ''} • 
                          {' '}{request.first_day_type.replace('_', ' ')} to {request.last_day_type.replace('_', ' ')}
                        </p>
                        {request.notes && (
                          <p className="text-xs sm:text-sm text-gray-700 italic mt-2 sm:mt-3 p-2 sm:p-3 bg-white/50 rounded break-words">
                            "{request.notes}"
                          </p>
                        )}
                        {request.review_note && (
                          <p className="text-xs sm:text-sm text-gray-700 mt-2 p-2 sm:p-3 bg-white/50 rounded break-words">
                            <span className="font-semibold">Manager:</span> {request.review_note}
                          </p>
                        )}
                      </div>

                      {/* Action Buttons */}
                      {(request.status === 'pending' || request.status === 'approved') && (
                        <div className="flex sm:flex-col items-center gap-2 w-full sm:w-auto sm:ml-4">
                          <Button
                            onClick={() => handleEditClick(request)}
                            size="sm"
                            variant="outline"
                            className="flex-1 sm:flex-none border-blue-500 text-blue-600 hover:bg-blue-50 text-xs sm:text-sm"
                          >
                            <Edit className="w-3 h-3 sm:w-4 sm:h-4 mr-1" />
                            Edit
                          </Button>
                          <Button
                            onClick={() => handleCancelRequest(request.id)}
                            size="sm"
                            variant="outline"
                            className="flex-1 sm:flex-none border-red-500 text-red-600 hover:bg-red-50 text-xs sm:text-sm"
                          >
                            <XCircle className="w-4 h-4 mr-1" />
                            Cancel
                          </Button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Time Off Request</DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* First Day */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit_first_day">First Day</Label>
                <Input
                  id="edit_first_day"
                  type="date"
                  value={editForm.first_day}
                  onChange={(e) => setEditForm({...editForm, first_day: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit_first_day_type">Type</Label>
                <select
                  id="edit_first_day_type"
                  value={editForm.first_day_type}
                  onChange={(e) => setEditForm({...editForm, first_day_type: e.target.value})}
                  className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="full_day">Full Day</option>
                  <option value="half_day">Half Day</option>
                  <option value="quarter_day">Quarter Day</option>
                </select>
              </div>
            </div>

            {/* Last Day */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="edit_last_day">Last Day</Label>
                <Input
                  id="edit_last_day"
                  type="date"
                  value={editForm.last_day}
                  onChange={(e) => setEditForm({...editForm, last_day: e.target.value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label htmlFor="edit_last_day_type">Type</Label>
                <select
                  id="edit_last_day_type"
                  value={editForm.last_day_type}
                  onChange={(e) => setEditForm({...editForm, last_day_type: e.target.value})}
                  className="mt-1 w-full h-10 px-3 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                >
                  <option value="full_day">Full Day</option>
                  <option value="half_day">Half Day</option>
                  <option value="quarter_day">Quarter Day</option>
                </select>
              </div>
            </div>

            {/* Notes */}
            <div>
              <Label htmlFor="edit_notes">Notes (Optional)</Label>
              <Textarea
                id="edit_notes"
                placeholder="Add or update notes..."
                value={editForm.notes}
                onChange={(e) => setEditForm({...editForm, notes: e.target.value})}
                rows={3}
                className="mt-1"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button
              onClick={() => setShowEditDialog(false)}
              variant="outline"
            >
              Cancel
            </Button>
            <Button
              onClick={handleEditSubmit}
              className="bg-teal-600 hover:bg-teal-700"
            >
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default ManageTimeOff;
